import React, { useEffect, useState } from 'react'
import { useAppStore } from '../../../store/app'
import {
  type AgentResourceGrant,
  type ManifestRuleApproval,
  type ManifestRuleWithStatus,
  createAgentResourceAccess,
  deleteAgentResourceGrant,
  fetchAgentResourceBindings,
  fetchAgentResourceGrantDefaultRules,
  fetchAgentResourceGrants,
  fetchPluginInstalls,
  fetchResourceConnections,
} from '../../../api/ws'
import type { AgentResourceBinding, ResourceConnection } from '../../../store/types'
import { InstallWizard } from '../../connections/InstallWizard'
import { NewConnectionWizard } from '../../connections/NewConnectionWizard'
import { SwipeableRow } from '../../../components/SwipeableRow'
import './ResourcesSegment.css'

// ResourcesSegment lists the Resource Connections an agent has been
// granted access to, with affordances to add or remove grants.
//
// Per the agent-resource-grant data-model discussion: there is no
// separate join table. "Granted" means "the IAM rules table has at
// least one allow/ask rule for the (agent, connection) pair." This
// segment talks to the agent_resource_grant_{list,create,delete}
// handlers, which write/read those rules under the hood.
//
// Granting seeds the plugin manifest's default_rules into the rules
// table scoped to this agent. Removing wipes them. Fine-grained
// per-verb editing IS surfaced: tap a granted row to open the
// InstallWizard in edit mode (openEditor below), where each verb has
// an allow/ask/deny dropdown. The one thing the editor deliberately
// won't do is delete an individual verb's rule — flip it to deny, or
// revoke + re-grant, or use the Rules debug sheet.
export function ResourcesSegment({
  agentInstanceID,
}: {
  agentInstanceID: string
}) {
  const agent = useAppStore((s) =>
    s.agentInstances.find((a) => a.id === agentInstanceID),
  )
  const allConnections = useAppStore((s) => s.resourceConnections)
  const allInstalls = useAppStore((s) => s.pluginInstalls)
  const hostConnections = allConnections.filter(
    (c) => c.host_id && agent?.host_id && c.host_id === agent.host_id,
  )
  const hostInstalls = allInstalls.filter(
    (p) => agent?.host_id && p.host_id === agent.host_id,
  )

  const [grants, setGrants] = useState<AgentResourceGrant[]>([])
  const [bindings, setBindings] = useState<AgentResourceBinding[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [newConnWizardOpen, setNewConnWizardOpen] = useState(false)
  // pickedConnection is the connection chosen in the picker; kept for the
  // wizard sheet's header.
  const [pickedConnection, setPickedConnection] = useState<ResourceConnection | null>(null)
  // Install-wizard state for the agent-grant flow. After the operator
  // picks a connection, we fetch the manifest defaults and
  // show the wizard so they can approve the per-agent rules
  // explicitly. See docs/iam-rule-types.md §"Plugin manifest
  // default_rules are suggestions, not auto-grants".
  const [wizardOpen, setWizardOpen] = useState(false)
  const [wizardManifest, setWizardManifest] = useState<ManifestRuleWithStatus[]>([])
  const [wizardLoading, setWizardLoading] = useState(false)
  // Per-grant fine-grained editor state. Set when the user taps a
  // row in the granted list; cleared by the editor's onCancel/onSave.
  // editorConnection holds the connection being edited so the editor
  // sheet has the metadata for header rendering and the save submit
  // target.
  const [editorConnection, setEditorConnection] = useState<AgentResourceGrant | null>(null)
  const [editorManifest, setEditorManifest] = useState<ManifestRuleWithStatus[]>([])
  const [editorLoading, setEditorLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // wizardError is shown inside the active WizardSheet so the user
  // sees failures without the modal disappearing first.
  const [wizardError, setWizardError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  async function refreshGrantsAndBindings() {
    try {
      const [grantsResp, bindingsResp] = await Promise.all([
        fetchAgentResourceGrants(agentInstanceID),
        fetchAgentResourceBindings(agentInstanceID),
      ])
      if (grantsResp.error) {
        setError(grantsResp.error)
      } else if (bindingsResp.error) {
        setError(bindingsResp.error)
      } else {
        setError(null)
      }
      setGrants(grantsResp.grants ?? [])
      setBindings(bindingsResp.agent_resource_bindings ?? [])
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    refreshGrantsAndBindings()
    fetchResourceConnections()
    fetchPluginInstalls()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentInstanceID])

  // Live binding for a given connection_id, if any. The picker shows
  // "bound as <label>" beside each granted row when this is set.
  const liveBindingsByConnection = new Map<string, AgentResourceBinding>()
  for (const b of bindings) {
    if (!b.revoked_at) liveBindingsByConnection.set(b.resource_connection_id, b)
  }

  const grantedIDs = new Set(grants.map((g) => g.resource_connection_id))
  const grantable = hostConnections.filter((c) => !grantedIDs.has(c.id))

  // The picker is one step now: choosing the connection settles the
  // credential too, since a connection authenticates as exactly one
  // account. Routes through the install wizard before the grant lands so
  // the operator can review the manifest's suggested rules.
  async function handlePickConnection(c: ResourceConnection) {
    setPickedConnection(c)
    await commitGrant(c.id)
  }

  async function commitGrant(resourceConnectionID: string) {
    setBusy(true)
    setError(null)
    setWizardLoading(true)
    try {
      const resp = await fetchAgentResourceGrantDefaultRules({
        agentInstanceID,
        resourceConnectionID,
      })
      if (resp.error) {
        setError(resp.error)
        return
      }
      const defaults = resp.manifest_defaults ?? []
      if (defaults.length === 0) {
        setError(
          "This connection's plugin manifest has no default_rules to grant. " +
            'Add at least one rule to the manifest, or use the rules editor to add agent rules by hand.',
        )
        return
      }
      setWizardManifest(defaults)
      setWizardOpen(true)
    } catch (e) {
      setError(String(e))
    } finally {
      setWizardLoading(false)
      setBusy(false)
    }
  }

  // Wizard's submit handler. Routes through the atomic access RPC
  // (agent_resource_access_create) — the same path the Household
  // Resource Connections sheet uses — so rules + binding + secret.use
  // land in one transaction, and the server auto-cycles the running
  // agent and emits the resource_granted system event. The older
  // grant+binding pair did none of that (no cycle, no system message,
  // no secret.use), which is why a grant made here used to sit inert
  // until a manual restart. No client-side cycle prompt needed now.
  async function approveAndMintGrant(rules: ManifestRuleApproval[]) {
    if (!pickedConnection) return
    setBusy(true)
    setWizardError(null)
    setError(null)
    try {
      const resp = await createAgentResourceAccess({
        resourceConnectionID: pickedConnection.id,
        aiAgentInstanceIDs: [agentInstanceID],
        rules,
        scope: 'agent',
      })
      if (resp.error) {
        setWizardError(resp.error)
        return
      }
      closePicker()
      await refreshGrantsAndBindings()
    } catch (e) {
      setWizardError(String(e))
    } finally {
      setBusy(false)
    }
  }

  function closePicker() {
    setPickerOpen(false)
    setPickedConnection(null)
    setWizardOpen(false)
    setWizardManifest([])
    setNewConnWizardOpen(false)
    setWizardError(null)
  }

  async function handleNewConnWizardDone() {
    setNewConnWizardOpen(false)
    setPickerOpen(false)
    await refreshGrantsAndBindings()
    fetchResourceConnections()
  }

  // Open the per-verb editor for a granted row. Fetches the manifest
  // defaults (with already_minted + minted_decision status) and hands
  // them to the InstallWizard in edit mode. Save flows through the same
  // createAgentResourceAccess RPC the grant path uses (rules UPSERT +
  // idempotent binding), so there's no second server endpoint to
  // maintain and an edit cycles/announces just like a grant.
  async function openEditor(g: AgentResourceGrant) {
    setEditorLoading(true)
    setError(null)
    try {
      const resp = await fetchAgentResourceGrantDefaultRules({
        agentInstanceID,
        resourceConnectionID: g.resource_connection_id,
      })
      if (resp.error) {
        setError(resp.error)
        return
      }
      const defaults = resp.manifest_defaults ?? []
      if (defaults.length === 0) {
        setError(
          "This connection's plugin manifest has no rules to edit. " +
            'Use the Rules debug sheet to add agent rules by hand.',
        )
        return
      }
      setEditorManifest(defaults)
      setEditorConnection(g)
    } catch (e) {
      setError(String(e))
    } finally {
      setEditorLoading(false)
    }
  }

  function closeEditor() {
    setEditorConnection(null)
    setEditorManifest([])
    setWizardError(null)
  }

  async function saveEditorChanges(rules: ManifestRuleApproval[]) {
    if (!editorConnection) return
    setBusy(true)
    setWizardError(null)
    setError(null)
    try {
      // Same atomic access RPC as the grant path, so editing an agent's
      // rules also cycles it + re-announces its (now updated) access —
      // a capability change should refresh the running agent, not sit
      // inert. The server's binding upsert is idempotent, so re-sending
      // for an already-bound agent only rewrites the rules.
      const resp = await createAgentResourceAccess({
        resourceConnectionID: editorConnection.resource_connection_id,
        aiAgentInstanceIDs: [agentInstanceID],
        rules,
        scope: 'agent',
      })
      if (resp.error) {
        setWizardError(resp.error)
        return
      }
      closeEditor()
      await refreshGrantsAndBindings()
    } catch (e) {
      setWizardError(String(e))
    } finally {
      setBusy(false)
    }
  }

  async function handleRemove(resourceConnectionID: string, displayName?: string) {
    if (
      !confirm(
        `Revoke access to "${displayName || resourceConnectionID}" for this agent? The default rules seeded at grant time will be removed.`,
      )
    ) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      const resp = await deleteAgentResourceGrant({
        agentInstanceID,
        resourceConnectionID,
      })
      if (resp.error) {
        setError(resp.error)
        return
      }
      await refreshGrantsAndBindings()
      // No client-side cycle prompt: the server's grant-delete handler
      // cycles the running agent and PTY-pastes a "no longer have access"
      // note, same as the grant path announces on add.
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="resources">
      <div className="resources__header">
        <div className="resources__title">Resources</div>
        {hostConnections.length > 0 && (
          <button
            className="resources__add"
            onClick={() => setPickerOpen(true)}
            disabled={busy}
          >
            + Add
          </button>
        )}
      </div>

      {error && <div className="resources__error">{error}</div>}

      {loading ? (
        <div className="resources__empty">Loading…</div>
      ) : grants.length === 0 ? (
        <EmptyState
          hasAnyOnHost={hostConnections.length > 0}
          hasInstalls={hostInstalls.length > 0}
          onAdd={() => setPickerOpen(true)}
          onNewConnection={() => setNewConnWizardOpen(true)}
        />
      ) : (
        <div className="resources__list">
          {grants.map((g) => {
            const binding = liveBindingsByConnection.get(g.resource_connection_id)
            const tappable = !busy && !editorLoading
            return (
              <SwipeableRow
                key={g.resource_connection_id}
                trailing={{
                  label: 'Remove',
                  onAction: () => handleRemove(g.resource_connection_id, g.display_name),
                }}
              >
                <button
                  type="button"
                  className="resources__row resources__row--tappable"
                  onClick={() => tappable && void openEditor(g)}
                  disabled={!tappable}
                >
                  <div className="resources__row-title">
                    {g.display_name || g.resource_connection_id}
                  </div>
                  <div className="resources__row-meta">
                    {g.plugin_slug}
                    {binding ? ' · bound' : ' · no binding'}
                  </div>
                </button>
              </SwipeableRow>
            )
          })}
        </div>
      )}

      {newConnWizardOpen && agent && (
        <NewConnectionWizard
          agentInstanceID={agentInstanceID}
          hostID={agent.host_id}
          pluginInstalls={hostInstalls}
          onDone={handleNewConnWizardDone}
          onCancel={() => setNewConnWizardOpen(false)}
        />
      )}
      {pickerOpen && !pickedConnection && !newConnWizardOpen && (
        <PickerSheet
          connections={grantable}
          onPick={handlePickConnection}
          onClose={closePicker}
          onNewConnection={hostInstalls.length > 0 ? () => { setPickerOpen(false); setNewConnWizardOpen(true) } : undefined}
          disabled={busy || wizardLoading}
        />
      )}
      {pickerOpen && pickedConnection && wizardOpen && (
        <WizardSheet
          title={`Approve rules — ${pickedConnection.display_name || pickedConnection.id}`}
          error={wizardError}
          onClose={closePicker}
        >
          <InstallWizard
            manifestDefaults={wizardManifest}
            scopeLabel="for this agent"
            submitLabel="Approve & grant access"
            busy={busy}
            onApprove={approveAndMintGrant}
            onCancel={closePicker}
          />
        </WizardSheet>
      )}
      {editorConnection && (
        <WizardSheet
          title={`Edit rules — ${editorConnection.display_name || editorConnection.resource_connection_id}`}
          error={wizardError}
          onClose={closeEditor}
        >
          {editorLoading ? (
            <div style={{ padding: '16px', opacity: 0.6 }}>Loading rules…</div>
          ) : (
            <InstallWizard
              manifestDefaults={editorManifest}
              scopeLabel="for this agent"
              submitLabel="Save changes"
              mode="edit"
              busy={busy}
              onApprove={saveEditorChanges}
              onCancel={closeEditor}
            />
          )}
        </WizardSheet>
      )}
    </div>
  )
}

// WizardSheet: bottom-sheet modal for the rules install/edit wizard.
// Taller than the plain picker sheets (85vh) because the wizard list
// can be long. The wizard's own Approve/Cancel buttons handle
// submission; the ✕ button in the header is an emergency escape hatch.
function WizardSheet({
  title,
  error,
  onClose,
  children,
}: {
  title: string
  error?: string | null
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div
      className="resources__picker-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="resources__picker resources__picker--wizard">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4, padding: '0 4px' }}>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{title}</div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, color: '#8e8e93', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {error && (
          <div className="resources__error" style={{ marginTop: 8, marginBottom: 0 }}>
            {error}
          </div>
        )}
        {/* Scrollable body so a long rule list (and the wizard's own
            Approve/Cancel footer) stays reachable — the sheet itself is
            overflow:hidden, so without this wrapper the footer buttons
            clip below the fold with no way to scroll to them. */}
        <div className="resources__picker-body" style={{ padding: 0 }}>
          {children}
        </div>
      </div>
    </div>
  )
}

function EmptyState({
  hasAnyOnHost,
  hasInstalls,
  onAdd,
  onNewConnection,
}: {
  hasAnyOnHost: boolean
  hasInstalls: boolean
  onAdd: () => void
  onNewConnection: () => void
}) {
  if (!hasAnyOnHost && !hasInstalls) {
    return (
      <div className="resources__empty">
        <div className="resources__empty-title">No plugins on this host</div>
        <div className="resources__empty-body">
          No plugins are installed on this agent's host yet. Install one
          under <code>~/.hearth/plugins/</code> and restart the daemon.
        </div>
      </div>
    )
  }
  if (!hasAnyOnHost) {
    return (
      <div className="resources__empty">
        <div className="resources__empty-title">No connections yet</div>
        <div className="resources__empty-body">
          Set up a connection to give this agent access to a plugin.
        </div>
        <button className="resources__empty-add" onClick={onNewConnection}>
          + New connection
        </button>
      </div>
    )
  }
  return (
    <div className="resources__empty">
      <div className="resources__empty-title">No resources granted</div>
      <div className="resources__empty-body">
        Grant this agent access to a Resource Connection to expose its
        plugin verbs in the agent's prompt.
      </div>
      <button className="resources__empty-add" onClick={onAdd}>
        + Add resource
      </button>
    </div>
  )
}

function PickerSheet({
  connections,
  onPick,
  onClose,
  onNewConnection,
  disabled,
}: {
  connections: ResourceConnection[]
  onPick: (c: ResourceConnection) => void
  onClose: () => void
  onNewConnection?: () => void
  disabled: boolean
}) {
  return (
    <div
      className="resources__picker-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="resources__picker">
        <div className="resources__picker-title">Grant a resource</div>
        <div className="resources__picker-list">
          {connections.map((c) => (
            <button
              key={c.id}
              className="resources__picker-item"
              onClick={() => onPick(c)}
              disabled={disabled}
            >
              <div className="resources__picker-item-title">
                {c.display_name || c.id}
              </div>
              <div className="resources__picker-item-meta">
                {c.plugin_slug ?? '?'} · {c.id}
              </div>
            </button>
          ))}
          {onNewConnection && (
            <button
              className="resources__picker-item"
              onClick={onNewConnection}
              disabled={disabled}
              style={{ borderTop: connections.length > 0 ? '1px dashed #ddd' : undefined }}
            >
              <div className="resources__picker-item-title">+ New connection</div>
              <div className="resources__picker-item-meta">
                Set up a plugin connection from scratch
              </div>
            </button>
          )}
        </div>
        <button className="resources__picker-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  )
}
